import { diceFaces, handLabel, playCeelo, type CeeloResult } from "../meta/ceelo";
import { ELITE_XP, VETERAN_XP, rankFor, traitsFor, vignetteFor } from "../meta/operators";
import type { SaveData } from "../meta/save";

// The Barracks (Personnel Office v0 — docs/design/HUB.md Room 4): the
// persistent roster as dossier cards. Read-only for now; deployment is
// automatic (readiest first). Pure renderer.

export class BarracksScreen {
  private readonly el: HTMLElement;
  private save: SaveData | null = null;
  // The blanket: session-only Cee-lo state. Matchsticks, not requisition —
  // the doc's hard rule is this can never touch meta progression.
  private lastGame: CeeloResult | null = null;
  private opponent = "";
  private matchsticks = 0;

  constructor(private readonly callbacks: { onBack: () => void }) {
    this.el = document.createElement("div");
    this.el.id = "barracks";
    this.el.className = "screen-overlay screen-overlay--stacked";
    this.el.style.zIndex = "350";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("#barracks-back")) this.callbacks.onBack();
      else if (target?.closest("#ceelo-roll")) this.rollCeelo();
    });
  }

  private rollCeelo(): void {
    const ready = (this.save?.operators ?? []).filter((op) => op.status === "ready");
    if (ready.length === 0) return;
    this.opponent = ready[Math.floor(Math.random() * ready.length)]!.name;
    this.lastGame = playCeelo(Math.random);
    if (this.lastGame.outcome === "win") this.matchsticks++;
    else if (this.lastGame.outcome === "lose") this.matchsticks--;
    this.render();
  }

  private renderBlanket(): string {
    const g = this.lastGame;
    const tally = this.matchsticks === 0
      ? "even on the blanket"
      : this.matchsticks > 0
        ? `up ${this.matchsticks} matchstick${this.matchsticks > 1 ? "s" : ""}`
        : `down ${-this.matchsticks} matchstick${this.matchsticks < -1 ? "s" : ""}`;
    const game = g
      ? `<div class="ceelo__game">
           <div class="ceelo__line">YOU &nbsp;${diceFaces(g.player.dice)} &nbsp;— ${handLabel(g.player.hand)}</div>
           <div class="ceelo__line">${this.opponent} &nbsp;${diceFaces(g.opponent.dice)} &nbsp;— ${handLabel(g.opponent.hand)}</div>
           <div class="ceelo__outcome ceelo__outcome--${g.outcome}">${
             g.outcome === "win" ? `${this.opponent} slides a matchstick over.`
             : g.outcome === "lose" ? `${this.opponent} grins and takes a matchstick.`
             : "Push. The blanket keeps its secrets."}</div>
         </div>`
      : `<div class="ceelo__line ceelo__line--idle">Off-duty operators are hunched over a wool blanket in the corner.</div>`;
    return `
      <div class="ceelo">
        <div class="bulletin-board__title">THE BLANKET — CEE-LO &nbsp;·&nbsp; you're ${tally}</div>
        ${game}
        <div class="mail-actions"><button id="ceelo-roll" class="screen-button screen-button--small screen-button--muted">THROW THE DICE</button></div>
      </div>`;
  }

  setProfile(save: SaveData): void {
    this.save = save;
    this.render();
  }

  private render(): void {
    const save = this.save;
    if (!save) return;

    const cards = save.operators.map((op) => {
      const rank = rankFor(op.xp);
      const nextAt = rank === "RECRUIT" ? VETERAN_XP : rank === "VETERAN" ? ELITE_XP : null;
      const xpLine = nextAt === null
        ? `XP ${op.xp} — MAX RANK`
        : `XP ${op.xp}/${nextAt}`;
      const traits = traitsFor(op.name, op.xp);
      const traitHtml = traits.length
        ? traits.map((t) => `<div class="op-trait" title="${t.desc}">▸ ${t.name}</div>`).join("")
        : `<div class="op-trait op-trait--none">NO TRAITS — RANK UP</div>`;
      const status = op.status === "wounded"
        ? `<span class="op-status op-status--wounded">WOUNDED (${op.woundedRuns} OP)</span>`
        : `<span class="op-status">READY</span>`;
      const vignette = vignetteFor(op.name, op.xp);
      const vignetteHtml = vignette
        ? `<div class="op-vignette">${vignette}</div>`
        : "";
      return `
        <div class="op-card op-card--${op.status}">
          <div class="op-card__head">
            <span class="op-card__name">${op.name}</span>
            <span class="op-rank op-rank--${rank.toLowerCase()}">${rank}</span>
          </div>
          <div class="op-card__xp">${xpLine} &nbsp; ${status}</div>
          ${traitHtml}
          ${vignetteHtml}
        </div>`;
    }).join("");

    this.el.innerHTML = `
      <div class="screen-kicker">PERSONNEL — 4TH SOS DETACHMENT</div>
      <div class="screen-title screen-title--compact">BARRACKS</div>
      <div class="screen-copy" style="text-align:center;max-width:560px">
        Operations deploy the readiest operators first. Casualties come home
        wounded and sit one out; a squad wipe is forever.
      </div>
      <div class="op-grid">${cards}</div>
      ${this.renderBlanket()}
      <div class="screen-actions" style="margin-top:12px">
        <button id="barracks-back" class="screen-button">BACK</button>
      </div>
    `;
  }

  show(): void {
    this.render();
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }
}
